import { Injectable } from '@angular/core';
import { BackendService } from 'app/core/backend.service';
import { CONFIG } from 'app/core/config';
import { HbNotification } from 'app/notifications/notification';
import { NotificationState } from 'app/notifications/notification-state';
import { NotificationService } from 'app/notifications/notification.service';
import { HierarchyTreeNode } from 'app/viewmodels/dashboard/hierarchy-tree-node';
import { OptimizationType } from 'app/viewmodels/equipment/enums/optimization-type';
import { TreeNode } from 'primeng/api';
import { Observable } from 'rxjs';
import { OrgTreeNodeType } from '../../../../viewmodels/admin/organizations/org-tree-node-type';
import { DashboardWidget } from '../../../../viewmodels/dashboard/dashboard-widget';
import { WidgetParameter } from '../../../../viewmodels/dashboard/widget-parameter';
import { DashboardService } from '../../dashboard.service';
import { RiskDistributionParameterType } from '../riskdistribution-parameter-type';
import { WidgetDataType } from '../widget-data-type';
import { ValueSource } from '../widget-parameters/value-source.enum';
import { ValueType } from '../widget-parameters/value-type.enum';
import { WidgetParametersService } from '../widget-parameters/widget-parameters.service';
;

@Injectable()
export class WidgetConfigurationService {
  private selectAllHierarchyNodes: boolean;
  private hierarchyParameters: WidgetParameter[] = [];
  private hierarchyNodeCount: number = 0;

  private _dashboardWidget: DashboardWidget;
  public hierarchyNodes: TreeNode[] = [];
  public selectedHierarchyNodes: any = [];
  public dashboardWidgetTitle: string;
  public riskDistributionParameter: WidgetParameter;
  public optimizationTypeParameter: WidgetParameter;
  public dateRangeStartParameter: WidgetParameter;
  public dateRangeEndParameter: WidgetParameter;

  set dashboardWidget(value: DashboardWidget) {
    this._dashboardWidget = value;
    this.setRiskDistributionWidgetParameter();
    this.setDateRangeWidgetParameters();
    this.setOptimizationTypeParameter();
  }

  get dashboardWidget() {
    return this._dashboardWidget;
  }

  constructor(
    public dashboardService: DashboardService,
    private widgetParametersService: WidgetParametersService,
    private backendService: BackendService,
    private notificationService: NotificationService,
  ) { }

  public getHierarchyTreeNodes(widgetDataType: string): Observable<HierarchyTreeNode[]> {
    return this.backendService.getAllById<HierarchyTreeNode>(HierarchyTreeNode, widgetDataType, CONFIG.apiRoutes.dashboardHierarchyTree);
  }

  public initialiseWidgetConfig(treeNodes: HierarchyTreeNode[]): void {
    this.dashboardWidgetTitle = this._dashboardWidget.widget.title;
    this.initialiseHierarchyTree(treeNodes);
  }

  public isSelectedHierarchyValid(): boolean {
    return (
      this.selectedHierarchyNodes.length > 0 &&
      this.selectedHierarchyNodes.findIndex(node => node.data.orgTreeNodeType !== OrgTreeNodeType.Organization) >= 0
    );
  }

  private initialiseHierarchyTree(hierarchyTreeNodes: HierarchyTreeNode[]): void {
    if (!hierarchyTreeNodes || !this._dashboardWidget) {
      return;
    }

    this.hierarchyNodeCount = 0;
    this.hierarchyNodes = [];
    this.selectedHierarchyNodes = [];

    this.hierarchyParameters = this.widgetParametersService.filterParameters(
      this._dashboardWidget.widget.widgetParameters,
      ValueSource.OrgTreeNodeId,
      ValueType.Guid,
    );

    this.selectAllHierarchyNodes = this.hierarchyParameters.length < 1;

    if (hierarchyTreeNodes) {
      hierarchyTreeNodes.forEach(orgNode => {
        const treeNode = this.CreateTreeNode(orgNode);
        if (orgNode.children) {
          this.processNodeChildren(orgNode, treeNode);
        }
        this.hierarchyNodes.push(treeNode);
      });
    }
  }

  private processNodeChildren(orgNode, treeNode: TreeNode) {
    if (orgNode.children) {
      orgNode.children.forEach(child => {
        const childNode = this.CreateTreeNode(child);
        if (child.children) {
          this.processNodeChildren(child, childNode);
        }
        if (!treeNode.children) {
          treeNode.children = [];
        }
        treeNode.children.push(childNode);
        treeNode.partialSelected = this.isPartialSelected(treeNode);
      });
    }
  }

  private isPartialSelected(treeNode: TreeNode) {
    return (
      !this.selectedHierarchyNodes.includes(treeNode) &&
      treeNode.children.findIndex(c => this.selectedHierarchyNodes.includes(c) || c.partialSelected) >= 0
    );
  }

  private CreateTreeNode(node: any): TreeNode {
    const newNode: TreeNode = { label: node.name, data: node };

    if (this.selectAllHierarchyNodes) {
      newNode.data.widgetParameterId = null;
      this.selectedHierarchyNodes.push(newNode);
    } else {
      const existingParam = this.hierarchyParameters.find(e => e.guidValue === node.orgTreeNodeId);
      if (existingParam) {
        newNode.data.widgetParameterId = existingParam.widgetParameterId;
        this.selectedHierarchyNodes.push(newNode);
      }
    }

    this.hierarchyNodeCount++;

    return newNode;
  }

  updateWidget(selectedOptimizationParameterValue: number) {
    this._dashboardWidget.widget.title = this.dashboardWidgetTitle;
    this.updateOptimizationTypeParameter(selectedOptimizationParameterValue);

    this._dashboardWidget.widget.widgetParameters = this._dashboardWidget.widget.widgetParameters.filter(
      wp => wp.valueSource !== ValueSource.OrgTreeNodeId,
    );

    if (this.hierarchyNodeCount !== this.selectedHierarchyNodes.length) {
      this.selectedHierarchyNodes.forEach(node => {
        const widgetParameterId = node.data.widgetParameterId || null;
        const { widgetId } = this._dashboardWidget;
        const param: WidgetParameter = this.widgetParametersService.createWidgetParameter(
          ValueSource.OrgTreeNodeId,
          ValueType.Guid,
          widgetId,
          widgetParameterId,
        );
        this.widgetParametersService.setWidgetParameterValue(param, node.data.orgTreeNodeId);
        this._dashboardWidget.widget.widgetParameters.push(param);
      });
    }

    this.dashboardService.updateWidget(this._dashboardWidget.widget);
  }

  private setDateRangeWidgetParameters(): void {
    if (!this._dashboardWidget) {
      return;
    }

    if (this._dashboardWidget.widget.widgetDataType === WidgetDataType.MitigationsDueByMonth) {
      this.dateRangeStartParameter = this.setDateRangeParameter(ValueSource.DateRangeStart, 1, 0);
      this.dateRangeEndParameter = this.setDateRangeParameter(ValueSource.DateRangeEnd, 31, 11);
    }
  }

  private setDateRangeParameter(source: ValueSource, defaultDay: number, defaultMonth: number): WidgetParameter {
    const dateRangeParam = this.widgetParametersService.filterParameters(this._dashboardWidget.widget.widgetParameters, source, ValueType.DateTime);

    if (dateRangeParam && dateRangeParam.length > 0) {
      return dateRangeParam[0];
    }

    const { widgetId } = this._dashboardWidget;
    const dateRangeParameter: WidgetParameter = this.widgetParametersService.createWidgetParameter(
      ValueSource.DateRangeEnd,
      ValueType.DateTime,
      widgetId,
    );

    let defaultDate = new Date();
    defaultDate = new Date(defaultDate.getFullYear(), defaultMonth, defaultDay);

    dateRangeParameter.dateTimeValue = defaultDate;
    this._dashboardWidget.widget.widgetParameters.push(dateRangeParameter);

    return dateRangeParameter;
  }

  private setRiskDistributionWidgetParameter(): void {
    if (this._dashboardWidget.widget.widgetDataType === WidgetDataType.RiskDistribution) {
      const riskDistributionWidgetParameters = this.widgetParametersService.filterParameters(
        this._dashboardWidget.widget.widgetParameters,
        ValueSource.RiskDistributionEnum,
        ValueType.Int,
      );
      // should be handled by API to always have a risk distribution parameter if widget data type is RiskDistribution
      // however as a precation we should have this check as well
      if (riskDistributionWidgetParameters && riskDistributionWidgetParameters.length > 0) {
        // should always be just one RiskDistribution widget parameter
        this.riskDistributionParameter = riskDistributionWidgetParameters[0];
      } else {
        const { widgetId } = this._dashboardWidget;
        this.riskDistributionParameter = this.widgetParametersService.createWidgetParameter(
          ValueSource.RiskDistributionEnum,
          ValueType.Int,
          widgetId,
        );
        this.widgetParametersService.setWidgetParameterValue(this.riskDistributionParameter, RiskDistributionParameterType.RiskBand);
      }
    }
  }

  private setOptimizationTypeParameter(): void {
    if (this._dashboardWidget.widget.widgetDataType === WidgetDataType.PlannedMaintenanceImprovements) {
      const optimizationTypeParameters = this.widgetParametersService.filterParameters(
        this._dashboardWidget.widget.widgetParameters,
        ValueSource.OptimizationTypeEnum,
        ValueType.Int,
      );
      this.optimizationTypeParameter = optimizationTypeParameters && optimizationTypeParameters.length > 0 ? optimizationTypeParameters[0] : null;
    }
  }

  private updateOptimizationTypeParameter(value: number): void {
    if (this._dashboardWidget.widget.widgetDataType === WidgetDataType.PlannedMaintenanceImprovements) {
      if (value in OptimizationType) {
        if (this.optimizationTypeParameter) {
          this.optimizationTypeParameter.intValue = value;
        } else {
          const { widgetId } = this._dashboardWidget;
          this.optimizationTypeParameter = this.widgetParametersService.createWidgetParameter(
            ValueSource.OptimizationTypeEnum,
            ValueType.Int,
            widgetId,
          );
          this.widgetParametersService.setWidgetParameterValue(this.optimizationTypeParameter, value);
          this.addDeleteWidgetParameter(this.optimizationTypeParameter, true);
        }
      } else {
        this.addDeleteWidgetParameter(this.optimizationTypeParameter, false);
        this.optimizationTypeParameter = null;
      }
    }
  }

  private addDeleteWidgetParameter(parameter: WidgetParameter, toAdd: boolean): void {
    if (this._dashboardWidget && this._dashboardWidget.widget && this._dashboardWidget.widget.widgetParameters) {
      const {
        widget: { widgetParameters },
      } = this._dashboardWidget;
      if (Array.isArray(widgetParameters)) {
        if (toAdd) {
          if (!widgetParameters.includes(parameter)) {
            widgetParameters.push(parameter);
          }
        } else {
          const index = widgetParameters.indexOf(parameter);
          if (index > -1) {
            widgetParameters.splice(index, 1);
          }
        }
      }
    }
  }

  public showNotification(description: string, state: NotificationState): void {
    const notification = new HbNotification();
    notification.description = description;
    notification.state = state;
    this.notificationService.displayToast(notification);
  }
}
